// biome-ignore-all lint/a11y/noNoninteractiveElementToInteractiveRole: li with role="option" inside a listbox is the standard ARIA listbox pattern
// biome-ignore-all lint/a11y/useKeyWithClickEvents: keyboard navigation handled by parent ScrollPane
"use client";

import {
  useState,
  useEffect,
  useRef,
  type FC,
  type KeyboardEventHandler,
  type KeyboardEvent,
} from "react";
import {
  isSameDay,
  startOfMonth,
  getDay,
  format,
  isToday,
  setYear,
  setMonth,
} from "date-fns";
import { isString, mergeClasses } from "@huub-web/utils";
import { useTranslation } from "react-i18next";

import { useFormattedDate } from "../../../hooks/useFormattedDate";
import { IconButton } from "../../IconButton";
import { Flex } from "../../Flex";
import { Text } from "../../Text";
import { Ruler } from "../../Ruler";
import { ScrollPane } from "../../ScrollPane";
import { YearField } from "../../YearField";
import { MonthField } from "../../MonthField";
import { Group } from "../../Group";

import {
  calendarStyle,
  dayCellStyle,
  weekdayHeaderStyle,
  emptyCellStyle,
  yearPickerStyle,
  monthPickerStyle,
  yearMonthStyle,
  containerStyle,
  dropdownContainerStyle,
  wrapperStyle,
} from "./DatePicker.css";
import {
  focusVisible,
  focusVisibleInset,
} from "../../../theme/css/focusVisible.css";
import { typography } from "../../../theme/css/typography.css";

export type DatePickerChangeHandler = (
  date: Date,
  formattedDate: string,
) => void;

export type DatePickerProps = {
  value?: Date;
  onChange?: DatePickerChangeHandler;
  height?: "static" | "auto";
  variant?: "dropdown" | "default" | "month";
  startOfWeek?: "sunday" | "monday";
  displayFormat?: string;
  disabled?: boolean;
};

/**
 * A simple date picker component that allows users to select a date.
 */
export const DatePicker: FC<DatePickerProps> = ({
  value,
  startOfWeek = "monday",
  variant = "default",
  height = "static",
  disabled,
  onChange,
}) => {
  const { t } = useTranslation();
  const { formatDate } = useFormattedDate();

  const [viewDate, setViewDate] = useState(
    !value || value?.toString() === "Invalid Date" ? new Date() : value,
  );

  const yearScrollRef = useRef<HTMLDivElement>(null);
  const monthScrollRef = useRef<HTMLDivElement>(null);

  const handleDayClick = (date: Date) => {
    if (disabled) {
      return;
    }
    change?.(date);
  };

  const handlePreviousMonth = () => {
    setViewDate(
      (prevDate) => new Date(prevDate.getFullYear(), prevDate.getMonth() - 1),
    );
  };
  const handleNextMonth = () => {
    setViewDate(
      (prevDate) => new Date(prevDate.getFullYear(), prevDate.getMonth() + 1),
    );
  };

  const handlePreviousYear = () => {
    setViewDate(
      (prevDate) => new Date(prevDate.getFullYear() - 1, prevDate.getMonth()),
    );
  };
  const handleNextYear = () => {
    setViewDate(
      (prevDate) => new Date(prevDate.getFullYear() + 1, prevDate.getMonth()),
    );
  };

  const change = (date: Date) => {
    const formattedDate = date ? formatDate(date) : "";
    onChange?.(date, formattedDate ?? "");
  };

  const monthYearLabel = formatDate(viewDate, { format: "monthYear" });

  const firstDayOfMonth = startOfMonth(viewDate);

  let startingDayIndex = getDay(firstDayOfMonth);
  if (startOfWeek === "monday") {
    startingDayIndex = startingDayIndex === 0 ? 6 : startingDayIndex - 1;
  }

  const daysInMonth = Array.from(
    {
      length: new Date(
        viewDate.getFullYear(),
        viewDate.getMonth() + 1,
        0,
      ).getDate(),
    },
    (_, i) => new Date(viewDate.getFullYear(), viewDate.getMonth(), i + 1),
  );

  const emptyCells = Array(startingDayIndex).fill(null);

  const weekdayLabels = (() => {
    let weekdayKeys = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
    if (startOfWeek === "monday") {
      weekdayKeys = [...weekdayKeys.slice(1), weekdayKeys[0]];
    }
    return weekdayKeys.map((key) => t(`dates.weekdays.${key}`));
  })();

  const currentYear = new Date().getFullYear();
  const years = (() => {
    const startYear = 1900;
    const endYear = 2100;

    return Array.from(
      { length: endYear - startYear + 1 },
      (_, i) => startYear + i,
    );
  })();

  const months = Array.from({ length: 12 }, (_, i) => i);

  const handleYearChange = (year: number) => {
    const newViewDate = setYear(viewDate, year);
    setViewDate(newViewDate);

    if (value) {
      const newDate = setYear(value, year);
      change?.(newDate);
    }
  };

  const handleMonthChange = (month: number) => {
    const newViewDate = setMonth(viewDate, month);
    setViewDate(newViewDate);

    if (value) {
      const newDate = setMonth(value, month);
      change?.(newDate);
    }
  };

  const firstScrollTo = useRef({ month: true, year: true });

  useEffect(() => {
    const scrollToSelected = (
      container: HTMLDivElement | null,
      selector: string,
      type: "month" | "year",
    ) => {
      if (!container) {
        return;
      }

      const selectedElement = container.querySelector(selector);
      if (!selectedElement) {
        return;
      }

      const elementRect = selectedElement.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      const scrollPosition =
        container.scrollTop +
        selectedElement.getBoundingClientRect().top -
        container.getBoundingClientRect().top -
        containerRect.height / 2 +
        elementRect.height / 2;

      container.scrollTo({
        top: scrollPosition,
        behavior: firstScrollTo.current[type] ? "instant" : "smooth",
      });

      firstScrollTo.current[type] = false;
    };

    scrollToSelected(
      yearScrollRef.current,
      `[data-year="${viewDate.getFullYear()}"]`,
      "month",
    );
    scrollToSelected(
      monthScrollRef.current,
      `[data-month="${viewDate.getMonth()}"]`,
      "year",
    );
  }, [viewDate]);

  const handleYearKeyDown: KeyboardEventHandler<HTMLElement> = (e) => {
    switch (e.key) {
      case "ArrowUp":
      case "ArrowLeft":
        handlePreviousYear();
        e.preventDefault();
        break;
      case "ArrowDown":
      case "ArrowRight":
        handleNextYear();
        e.preventDefault();
        break;
    }
  };

  const handleMonthKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
    switch (e.key) {
      case "ArrowUp":
      case "ArrowLeft":
        handlePreviousMonth();
        e.preventDefault();
        break;
      case "ArrowDown":
      case "ArrowRight":
        handleNextMonth();
        e.preventDefault();
        break;
    }
  };

  const handleDayKeyDown = (e: KeyboardEvent<HTMLDivElement>, day: Date) => {
    switch (e.key) {
      case "Enter":
      case " ":
        e.preventDefault();
        handleDayClick(day);
        break;
    }
  };

  return (
    <Flex
      gap="xs"
      className={mergeClasses(
        containerStyle({ height, disabled }),
        typography.bodyM,
      )}
      role="application"
      aria-label={t("dates.datePicker")}
    >
      {variant === "default" && (
        <>
          <ScrollPane
            ref={yearScrollRef}
            overlapColor="background"
            hideScrollbar
            role="listbox"
            aria-label={t("dates.year")}
            tabIndex={0}
            innerTabIndex={-1}
            className={mergeClasses(yearPickerStyle, focusVisible)}
            onKeyDown={handleYearKeyDown}
            aria-activedescendant={`year-${viewDate.getFullYear()}`}
          >
            <Flex as="ul" direction="column" gap="xxs2" tabIndex={-1}>
              {years.map((year) => {
                const selected = year === viewDate.getFullYear();
                const current = year === currentYear && !selected;

                return (
                  <li
                    key={year}
                    id={`year-${year}`}
                    data-year={year}
                    role="option"
                    aria-selected={selected}
                    tabIndex={-1}
                    className={mergeClasses(
                      yearMonthStyle({
                        selected,
                        default: !selected,
                        current,
                      }),
                    )}
                    onClick={() => handleYearChange(year)}
                  >
                    {year}
                  </li>
                );
              })}
            </Flex>
          </ScrollPane>

          <Ruler direction="vertical" />

          <ScrollPane
            ref={monthScrollRef}
            overlapColor="background"
            hideScrollbar
            role="listbox"
            aria-label={t("dates.month")}
            tabIndex={0}
            innerTabIndex={-1}
            className={mergeClasses(monthPickerStyle, focusVisible)}
            onKeyDown={handleMonthKeyDown}
            aria-activedescendant={`month-${viewDate.getMonth()}`}
          >
            <Flex as="ul" direction="column" gap="xxs2">
              {months.map((month) => {
                const selected = month === viewDate.getMonth();
                const current =
                  month === new Date().getMonth() &&
                  viewDate.getFullYear() === new Date().getFullYear() &&
                  !selected;

                return (
                  <li
                    key={month}
                    id={`month-${month}`}
                    data-month={month}
                    role="option"
                    aria-selected={selected}
                    tabIndex={-1}
                    className={mergeClasses(
                      yearMonthStyle({
                        selected,
                        default: !selected,
                        current,
                      }),
                    )}
                    onClick={() => handleMonthChange(month)}
                  >
                    {t(
                      `dates.months.short.${format(new Date(2024, month), "MMM").toLowerCase()}`,
                    )}
                  </li>
                );
              })}
            </Flex>
          </ScrollPane>

          <Ruler direction="vertical" />
        </>
      )}

      <Flex direction="column" gap="s" className={wrapperStyle}>
        <Flex gap="xxs" align="center">
          <IconButton
            icon="chevronLeft"
            onClick={handlePreviousMonth}
            aria-label={t("dates.previousMonth")}
            size="xxs"
            variant="opaque"
            border="default"
            className={focusVisibleInset}
            rounded
          />

          {(variant === "default" || variant === "month") && (
            <Text ellipsis align="center" aria-live="polite">
              {monthYearLabel}
            </Text>
          )}

          {variant === "dropdown" && (
            <Flex
              grow
              direction="column"
              align="center"
              justify="center"
              gap="xxs"
              className={dropdownContainerStyle}
            >
              <Group>
                <MonthField
                  trailingLast={null}
                  value={viewDate.getMonth()}
                  variant="default"
                  size="s"
                  labelVariant="short"
                  onChange={(month) =>
                    handleMonthChange(
                      isString(month) ? parseInt(month, 10) : (month as number),
                    )
                  }
                />
                <YearField
                  trailingLast={null}
                  value={viewDate.getFullYear()}
                  onChange={(year) =>
                    handleYearChange(
                      isString(year) ? parseInt(year, 10) : (year as number),
                    )
                  }
                  variant="default"
                  size="s"
                />
              </Group>
            </Flex>
          )}

          <IconButton
            rounded
            icon="chevronRight"
            onClick={handleNextMonth}
            aria-label={t("dates.nextMonth")}
            size="xxs"
            className={focusVisibleInset}
            variant="opaque"
            border="default"
          />
        </Flex>

        {/* biome-ignore lint/a11y/useSemanticElements: calendar grid requires div with role="grid" for proper ARIA pattern */}
        <div
          className={mergeClasses(calendarStyle)}
          role="grid"
          aria-label={t("dates.calendar")}
          aria-activedescendant={
            value && !Number.isNaN(value.getTime())
              ? `day-${value.toISOString()}`
              : undefined
          }
          tabIndex={0}
        >
          {weekdayLabels.map((day) => (
            <Text
              key={day}
              className={weekdayHeaderStyle}
              role="columnheader"
              color="currentColor25"
              aria-label={day}
            >
              {day}
            </Text>
          ))}

          {emptyCells.map((_, index) => (
            // biome-ignore lint/a11y/useSemanticElements: gridcell role required for calendar ARIA grid pattern
            <div
              key={`empty-${index}`}
              className={emptyCellStyle}
              role="gridcell"
              tabIndex={-1}
              aria-hidden="true"
            />
          ))}

          {daysInMonth.map((day) => {
            const selected = value && isSameDay(day, value);
            const current = value && isToday(day) && !selected;

            return (
              // biome-ignore lint/a11y/useSemanticElements: gridcell role required for calendar ARIA grid pattern
              <div
                key={day.toISOString()}
                id={`day-${day.toISOString()}`}
                role="gridcell"
                aria-selected={selected}
                aria-current={current ? "date" : undefined}
                tabIndex={0}
                className={mergeClasses(
                  dayCellStyle({
                    default: !selected,
                    selected: selected,
                    today: current,
                  }),
                  focusVisibleInset,
                )}
                onClick={() => handleDayClick(day)}
                onKeyDown={(e) => handleDayKeyDown(e, day)}
              >
                {day.getDate()}
              </div>
            );
          })}
        </div>
      </Flex>
    </Flex>
  );
};
